import { useState, Fragment } from 'react';
import { trickleCreateObject } from '../../utils/api';

function UploadModal({ onClose }) {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        file: null,
        coverImage: null
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    try {

        const handleChange = (e) => {
            const { name, value, files } = e.target;
            if (files) {
                if (name === 'file') {
                    const file = files[0];
                    if (file && file.type !== 'application/pdf') {
                        setError('Please upload a PDF file');
                        return;
                    }
                    if (file && file.size > 50 * 1024 * 1024) {
                        setError('File size should be less than 50MB');
                        return;
                    }
                    setFormData(prev => ({ ...prev, [name]: file }));
                } else if (name === 'coverImage') {
                    const file = files[0];
                    if (file && !file.type.startsWith('image/')) {
                        setError('Please upload an image file');
                        return;
                    }
                    if (file && file.size > 5 * 1024 * 1024) {
                        setError('Image size should be less than 5MB');
                        return;
                    }
                    setFormData(prev => ({ ...prev, [name]: file }));
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setPreviewUrl(reader.result);
                    };
                    reader.readAsDataURL(file);
                }
            } else {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
            setError('');
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');

            if (!formData.title.trim()) {
                setError('Please enter a title');
                return;
            }
            if (!formData.description.trim()) {
                setError('Please enter a description');
                return;
            }
            if (!formData.category) {
                setError('Please select a category');
                return;
            }
            if (!formData.file) {
                setError('Please upload a PDF file');
                return;
            }

            setIsLoading(true);

            try {
                const fileBase64 = await fileToBase64(formData.file);
                const coverImageBase64 = formData.coverImage ? 
                    await fileToBase64(formData.coverImage) : 
                    null;

                const bookData = {
                    title: formData.title,
                    description: formData.description,
                    category: formData.category,
                    file: fileBase64,
                    coverImage: coverImageBase64 || 'https://via.placeholder.com/300x400',
                    uploadedAt: new Date().toISOString()
                };

                await trickleCreateObject('book:public', bookData);
                onClose();
                window.location.reload();
            } catch (err) {
                setError('Failed to upload book. Please try again.');
                console.error('Upload error:', err);
            } finally {
                setIsLoading(false);
            }
        };

        const fileToBase64 = (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-name="upload-modal">
                <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl" data-name="modal-content">
                    <div className="p-8">
                        <div className="flex justify-between items-center mb-8">
                            <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                                Upload New Book
                            </h2>
                            <button 
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                disabled={isLoading}
                                data-name="close-button"
                            >
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>

                        {error && (
                            <div 
                                className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded" 
                                role="alert"
                                data-name="error-message"
                            >
                                <div className="flex">
                                    <i className="fas fa-exclamation-circle text-red-400 mr-3"></i>
                                    <p className="text-red-700">{error}</p>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                                        Book Title
                                    </label>
                                    <input
                                        type="text"
                                        id="title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                                        placeholder="Enter book title"
                                        disabled={isLoading}
                                        data-name="title-input"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all h-32"
                                        placeholder="Enter book description"
                                        disabled={isLoading}
                                        data-name="description-input"
                                    ></textarea>
                                </div>

                                <div>
                                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                                        Category
                                    </label>
                                    <select
                                        id="category"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                                        disabled={isLoading}
                                        data-name="category-input"
                                    >
                                        <option value="">Select Category</option>
                                        <option value="fiction">Fiction</option>
                                        <option value="non-fiction">Non-Fiction</option>
                                        <option value="science">Science</option>
                                        <option value="technology">Technology</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-2">
                                        PDF File
                                    </label>
                                    <div className="flex items-center justify-center w-full">
                                        <label
                                            htmlFor="file"
                                            className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all"
                                        >
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
                                                <p className="mb-2 text-sm text-gray-500">
                                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                                </p>
                                                <p className="text-xs text-gray-500">PDF (MAX. 50MB)</p>
                                            </div>
                                            <input
                                                type="file"
                                                id="file"
                                                name="file"
                                                onChange={handleChange}
                                                accept=".pdf"
                                                className="hidden"
                                                disabled={isLoading}
                                                data-name="file-input"
                                            />
                                        </label>
                                    </div>
                                    {formData.file && (
                                        <p className="text-sm text-green-600 mt-2">
                                            <i className="fas fa-check-circle mr-2"></i>
                                            {formData.file.name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="coverImage" className="block text-sm font-medium text-gray-700 mb-2">
                                        Cover Image (Optional)
                                    </label>
                                    <div className="flex items-center space-x-4">
                                        <div className="flex-1">
                                            <label
                                                htmlFor="coverImage"
                                                className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-all"
                                            >
                                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                    <i className="fas fa-image text-3xl text-gray-400 mb-2"></i>
                                                    <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 5MB)</p>
                                                </div>
                                                <input
                                                    type="file"
                                                    id="coverImage"
                                                    name="coverImage"
                                                    onChange={handleChange}
                                                    accept="image/*"
                                                    className="hidden"
                                                    disabled={isLoading}
                                                    data-name="cover-image-input"
                                                />
                                            </label>
                                        </div>
                                        {previewUrl && (
                                            <div className="w-32 h-32 relative">
                                                <img 
                                                    src={previewUrl} 
                                                    alt="Cover preview" 
                                                    className="w-full h-full object-cover rounded-lg"
                                                    data-name="cover-preview"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-4 pt-6 border-t">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all"
                                    disabled={isLoading}
                                    data-name="cancel-button"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all flex items-center"
                                    disabled={isLoading}
                                    data-name="submit-button"
                                >
                                    {isLoading ? (
                                        <Fragment>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                            Uploading...
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <i className="fas fa-cloud-upload-alt mr-2"></i>
                                            Upload Book
                                        </Fragment>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    } catch (error) {
        console.error('UploadModal component error:', error);
        reportError(error);
        return null;
    }
}

export default UploadModal;